import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import NoData from '@src/components/NoData';
import createLimiter from '@src/services/limiter';
import actions from '@src/redux/actions';
import ROUTES from '@src/constants/route';
import ProjectCard from './ProjectCard';
import CardSkeleton from './CardSkeleton';
import useRecentProjects from './useRecentProjects';
import { StyledProjectGrid } from './index.style';

const PROJECTS_LIMIT = 10;
// One placeholder per project on the way, so the grid keeps its final shape.
const SKELETON_KEYS = Array.from(
  { length: PROJECTS_LIMIT },
  (_, index) => `skeleton-${index}`,
);

// Load the next batch a bit before the end, so the scroll does not stall.
const LOAD_AHEAD = '300px';

// A screenful of cards comes into view together, each reading a ~640KB moov
// before it decodes. Three keeps mediaExtractor's two decoder slots fed.
const MAX_PARALLEL_EXTRACTS = 3;

const ProjectGrid = ({
  search,
  removedIds,
  selected,
  onSelect,
  onPageItems,
  onDelete,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const sentinelRef = useRef(null);

  const { projects, loading, hasMore, loadMore } = useRecentProjects({
    search,
    removedIds,
    defaultRowsPerPage: PROJECTS_LIMIT,
    append: true,
  });

  // One for the whole grid, not one per card — the cap only means something if
  // the cards share it. Held here so leaving the page drops it, queue and all.
  const limitRef = useRef(createLimiter(MAX_PARALLEL_EXTRACTS));

  // Only one card menu at a time, so it is held here rather than in each card.
  const [openMenuId, setOpenMenuId] = useState(null);

  // The selection bar lives in the toolbar above, so it needs the loaded cards.
  useEffect(() => onPageItems(projects), [projects]);

  // Ids, so the lookup does not walk the selection for every card on screen.
  const selectedIds = useMemo(
    () => new Set(selected.map((project) => project.id)),
    [selected],
  );

  // Load more when the end of the grid comes into view. Not while a batch is
  // loading, so requests do not pile up.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || loading || !hasMore) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadMore();
      },
      { rootMargin: LOAD_AHEAD },
    );
    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [loading, hasMore, loadMore]);

  // The detail page picks its layout from project.product.
  // useCallback so the cards keep their memo when a batch lands.
  const handleOpenProject = useCallback(
    (project) => {
      dispatch(actions.project.setProduct(project.product));
      history.push(`${ROUTES.PROJECTS}/${project.id}`);
    },
    [dispatch, history],
  );

  const handleToggleSelect = useCallback(
    (project) => {
      // The checkbox stops the click, so ClickAwayListener never hears it.
      setOpenMenuId(null);
      onSelect((prev) =>
        prev.some((item) => item.id === project.id)
          ? prev.filter((item) => item.id !== project.id)
          : [...prev, project],
      );
    },
    [onSelect],
  );

  const handleMenuOpenChange = useCallback(
    (id, next) => setOpenMenuId(next ? id : null),
    [],
  );

  if (!loading && !projects.length) {
    return <NoData height="326px" noDataText={t('projectListEmpty')} />;
  }

  return (
    <>
      <StyledProjectGrid>
        {projects.map((project) => (
          <ProjectCard
            key={project.id}
            project={project}
            selected={selectedIds.has(project.id)}
            menuOpen={openMenuId === project.id}
            onOpen={handleOpenProject}
            onToggleSelect={handleToggleSelect}
            onMenuOpenChange={handleMenuOpenChange}
            onDelete={onDelete}
            limitExtracts={limitRef.current}
          />
        ))}
        {loading && SKELETON_KEYS.map((key) => <CardSkeleton key={key} />)}
      </StyledProjectGrid>
      <div ref={sentinelRef} />
    </>
  );
};

// Same reason as the list: a keystroke must not redraw every card.
export default React.memo(ProjectGrid);
